import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import type { ModeDeLaCarte } from '@nextilt/commun';
import type { PoseDesPapiers, RegroupementDeLaCarte } from '../carte';
import type { Feuille } from '../partage/api/types';
import { api } from '../partage/api/client';
import { dureeLisible } from '../partage/datesEnMots';
import { SANS_LOUPE, type BulleEnAnalyse, type LoupeDeLaCarte } from '../partage/loupeDeLaCarte';
import { compteARebours, environ, titreDeColonne } from '../partage/vocabulaire';
import { useGroupes } from '../partage/useArbre';
import { groupesALEcran, regroupementSuivant, type Regroupement } from './groupesALEcran';
import { rienNEcouteEchap } from '../partage/useEchap';
import { infosAuLieuDuTitre, trouveeDans } from './infosDesBulles';
import { gesteDeLaTouche, type InfoDUneBulle, type Marge } from './modes';

/** Rien d'ouvert sur place : la profondeur 1. Une seule liste, pour que les groupes ne se recalculent pas. */
const AUCUNE_OUVERTE: readonly string[] = [];

/** On écrit : un champ, une zone de texte, un texte modifiable. Aucune touche n'y part. */
export function enTrainDEcrire(cible: EventTarget | null): boolean {
  const element = cible as HTMLElement | null;
  return element?.tagName === 'INPUT' || element?.tagName === 'TEXTAREA' || element?.isContentEditable === true;
}

/**
 * **Le gestionnaire de touches de la carte, le seul** (chantier modes-de-la-carte, modification
 * 02, décisions d'Igor du 2026-10-21) : sur la Carte et la Hiérarchie d'un projet, à
 * l'ordinateur, il lit la table des touches du mode du projet (`modes.ts`) et tient ce qu'elles
 * changent, pour les deux vues : l'info montrée à la place des titres, la recherche.
 *
 * - D, T, C montrent une info (Réflexion) ou deux (Exécution) ; la même touche remet les titres,
 *   une autre la remplace ;
 * - P, L posent un post-it, une liste, là où « + Post-it » et « + Liste » les posent (la Carte
 *   seulement : la Hiérarchie n'a pas de papiers) ;
 * - / ouvre la recherche ; M, R, F ouvrent et ferment leur marge, plusieurs à la fois
 *   (modification 03) ; Échap remet les titres, ferme la recherche et les marges ;
 * - A montre les liens cachés de la carte, en pâle, puis les recache (chantier liens-entre-bulles,
 *   ticket 03) : une bascule, la Carte seulement ; Échap les recache avec le reste. Chaque bascule
 *   est une lecture de l'app pour le journal des gestes (`montrer_liens_caches`,
 *   `recacher_liens_caches`, sur la feuille montrée) ;
 * - H lance Hiérarchiser par son écoute à elle, déjà là (`useHierarchisation`) : rien ici, pour
 *   ne pas lancer et arrêter d'un même coup ;
 * - J lance « Lier » (chantier liens-entre-bulles, ticket 04), et l'arrête s'il est en cours : la
 *   feuille en décide (`lier`), qui sait si rien d'autre n'est ouvert ; c'est la seule touche qui
 *   passe pendant que « Lier » écoute Échap ;
 * - en Analyse (chantier theme-analyse, ticket 02), le panneau d'analyse est ouvert d'office, M le
 *   ferme et le rouvre, Échap le rouvre avec le reste ; une ligne survolée ou une bulle cliquée
 *   s'allume, et les bulles disent leur durée et leur J-n ;
 * - en Analyse, sur la Carte seulement (ticket 03), G regroupe les bulles en colonnes : état,
 *   contact, lieu, mois, puis la carte d'avant ; Échap revient à la carte d'avant. La Hiérarchie
 *   garde son dessin, sans colonnes (cadrage du chantier) : G n'y fait rien.
 *
 * Aucune touche ne part pendant qu'on écrit, avec Ctrl, Alt ou Cmd, ni tant que quelque chose
 * écoute Échap (une fiche, un panneau, un menu, Hiérarchiser) : Échap le ferme d'abord, comme
 * aujourd'hui (`rienNEcouteEchap`). `actives` : la Carte ou la Hiérarchie, sans fiche ni
 * inventaire ouverts, à l'ordinateur.
 */
export function useTouchesDeLaCarte({
  mode,
  actives,
  projet,
  poseDesPapiers,
  feuille,
  surLaCarte = false,
  ouvertes = AUCUNE_OUVERTE,
  lier,
}: {
  mode: ModeDeLaCarte;
  actives: boolean;
  /** La vue Carte (et non la Hiérarchie) : la seule où l'on regroupe. */
  surLaCarte?: boolean;
  /** Le projet ouvert : en changer remet tout. */
  projet: string | null;
  poseDesPapiers: MutableRefObject<PoseDesPapiers | null>;
  /** La feuille ouverte : en Analyse, ce que disent ses bulles et son centre. */
  feuille: Feuille | undefined;
  /**
   * Ce qui est ouvert sur place à la profondeur choisie (`ProfondeurIci.ouvertes`) : en colonnes,
   * les groupes rangent toutes les bulles que la carte montre (ticket 03 rouvert, choix A d'Igor).
   */
  ouvertes?: readonly string[];
  /**
   * J : lancer ou arrêter « Lier » ; `libre` : rien n'écoute Échap. Rend vrai si la touche a servi.
   */
  lier?: (libre: boolean) => boolean;
}) {
  // La dernière décision de la feuille, lue au moment de la touche (l'écoute est posée une fois).
  const lierIci = useRef(lier);
  useLayoutEffect(() => {
    lierIci.current = lier;
  });
  /** La touche d'info enfoncée et ce qu'elle montre ; `null` : les titres. */
  const [infos, setInfos] = useState<{ cle: string; infos: readonly InfoDUneBulle[] } | null>(null);
  /** Ce qui est tapé dans la recherche ; `null` : fermée. */
  const [recherche, setRecherche] = useState<string | null>(null);
  const champ = useRef<HTMLInputElement>(null);
  /** Les marges ouvertes (modification 03). */
  const [marges, setMarges] = useState<ReadonlySet<Marge>>(new Set());
  /** Le contact choisi dans le répertoire : ses bulles s'allument ; `null` : aucun. */
  const [contact, setContact] = useState<{ cle: string; bulles: ReadonlySet<string> } | null>(null);
  /** En Analyse : le panneau d'analyse, ouvert d'office (theme-analyse, ticket 02). */
  const [panneau, setPanneau] = useState(true);
  /** En Analyse : la bulle allumée par une ligne survolée ou un clic ; `null` : aucune. */
  const [surlignee, setSurlignee] = useState<string | null>(null);
  /** En Analyse, sur la Carte : le regroupement des colonnes (ticket 03) ; `null` : la carte d'avant. */
  const [regroupement, setRegroupement] = useState<Regroupement | null>(null);
  /** Un bloc de groupe survolé dans le panneau : ses bulles s'allument ; `null` : aucun. */
  const [groupeVu, setGroupeVu] = useState<{ cle: string; bulles: ReadonlySet<string> } | null>(null);
  /** A : les liens cachés de la carte se montrent (ticket 03 de liens-entre-bulles). */
  const [liensCaches, setLiensCaches] = useState(false);
  /*
   * La bascule de A, et le signal au journal (une lecture de l'app). Lue dans une référence : la
   * touche part d'une écoute posée une fois, et le signal ne se répète pas si React rejoue.
   */
  const liensCachesMontres = useRef(false);
  const feuilleMontree = feuille?.bulle.id ?? null;
  const basculerLesLiensCaches = useCallback(
    (montrer: boolean) => {
      if (liensCachesMontres.current === montrer) return;
      liensCachesMontres.current = montrer;
      setLiensCaches(montrer);
      if (feuilleMontree) {
        api.signalerUneLecture({
          geste: montrer ? 'montrer_liens_caches' : 'recacher_liens_caches',
          noeudId: feuilleMontree,
          angle: 'carte',
        });
      }
    },
    [feuilleMontree]
  );

  const remettre = useCallback(() => {
    setInfos(null);
    setRecherche(null);
    setMarges(new Set());
    setContact(null);
    setPanneau(true);
    setSurlignee(null);
    setRegroupement(null);
    setGroupeVu(null);
  }, []);

  // Un autre projet, un autre mode : les titres reviennent, la recherche se ferme.
  const [vu, setVu] = useState({ projet, mode });
  if (vu.projet !== projet || vu.mode !== mode) {
    setVu({ projet, mode });
    remettre();
    // Les liens cachés se recachent sans signal : le projet ou le mode qui change en dit assez.
    liensCachesMontres.current = false;
    setLiensCaches(false);
  }
  // Quitter la Carte (la Hiérarchie, la Liste…) : on ne regroupe plus, la carte d'avant reviendra.
  const [carteVue, setCarteVue] = useState(surLaCarte);
  if (carteVue !== surLaCarte) {
    setCarteVue(surLaCarte);
    setRegroupement(null);
    setGroupeVu(null);
    liensCachesMontres.current = false;
    setLiensCaches(false);
  }

  useEffect(() => {
    if (!actives) return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (enTrainDEcrire(e.target)) return;
      const geste = gesteDeLaTouche(mode, e.key);
      if (geste?.sorte === 'lier') {
        if (lierIci.current?.(rienNEcouteEchap())) e.preventDefault();
        return;
      }
      if (!rienNEcouteEchap()) return;
      if (!geste) return;
      switch (geste.sorte) {
        case 'infos': {
          e.preventDefault();
          const cle = e.key.toLowerCase();
          setInfos((avant) => (avant?.cle === cle ? null : { cle, infos: geste.infos }));
          return;
        }
        case 'papier': {
          const pose = poseDesPapiers.current;
          if (!pose) return;
          e.preventDefault();
          if (geste.papier === 'post_it') pose.postIt();
          else pose.liste();
          return;
        }
        case 'chercher':
          e.preventDefault();
          setRecherche((avant) => avant ?? '');
          // Le champ se montre au rendu suivant : le curseur y va dès qu'il est là.
          requestAnimationFrame(() => champ.current?.focus());
          return;
        case 'marge': {
          e.preventDefault();
          const marge = geste.marge;
          setMarges((avant) => {
            const apres = new Set(avant);
            if (apres.has(marge)) apres.delete(marge);
            else apres.add(marge);
            return apres;
          });
          // Le répertoire refermé éteint le contact choisi.
          if (marge === 'repertoire') setContact(null);
          return;
        }
        case 'analyse':
          e.preventDefault();
          setPanneau((avant) => !avant);
          return;
        case 'remettre':
          remettre();
          basculerLesLiensCaches(false);
          return;
        case 'liens_caches':
          if (!surLaCarte) return;
          e.preventDefault();
          basculerLesLiensCaches(!liensCachesMontres.current);
          return;
        case 'regrouper':
          if (!surLaCarte) return;
          e.preventDefault();
          setRegroupement(regroupementSuivant);
          setGroupeVu(null);
          return;
        // H : l'écoute de Hiérarchiser, déjà là ; J, plus haut.
        default:
          return;
      }
    };
    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [actives, mode, poseDesPapiers, remettre, surLaCarte, basculerLesLiensCaches]);

  const basculerLaBulle = useCallback((id: string) => setSurlignee((avant) => (avant === id ? null : id)), []);

  const enAnalyse = mode === 'analyse';
  /* En Analyse : ce que dit chaque bulle, et le centre, comptés par le serveur (`Feuille.analyse`). */
  const analyse = useMemo<LoupeDeLaCarte['analyse']>(() => {
    if (!enAnalyse || !feuille) return null;
    const parBulle = feuille.analyse.bulles;
    const reste = feuille.analyse.tempsQuiReste;
    return {
      deLaBulle: (bulle): BulleEnAnalyse | null => {
        const lue = parBulle[bulle.id];
        if (!lue) return null;
        return {
          duree: lue.minutes === null ? null : dureeLisible(lue.minutes),
          rebours: lue.date ? compteARebours(lue.date.dans) : null,
          lueur: lue.date?.lueur ?? null,
        };
      },
      duCentre: reste > 0 ? environ(dureeLisible(reste)) : null,
    };
  }, [enAnalyse, feuille]);

  /*
   * Les groupes du regroupement, comptés par le serveur (`useGroupes`) et lus une fois pour les
   * colonnes et le panneau, sur les bulles que la carte montre à la profondeur choisie : la changer
   * refait les colonnes, et la caméra recadre (la clé du cadrage porte ce qui est ouvert). Le temps
   * de la réponse, les groupes d'avant restent, avec le choix et l'ouverture qui les ont faits.
   */
  const regroupe = enAnalyse && surLaCarte ? regroupement : null;
  const lus = useGroupes(feuille?.bulle.id ?? null, regroupe, ouvertes).data;
  const groupesLus = regroupe && lus ? lus : null;
  const groupes = useMemo(
    () => (groupesLus && feuille ? groupesALEcran(groupesLus.groupes, groupesLus.par, feuille) : null),
    [groupesLus, feuille]
  );
  const colonnes = useMemo<RegroupementDeLaCarte | null>(
    () =>
      groupesLus && groupes
        ? {
            cle: `${groupesLus.par} ${groupesLus.ouvertes.join(' ')}`,
            colonnes: groupes.map((g) => ({
              cle: g.cle,
              titre: titreDeColonne(g.nom, g.bulles.length),
              ids: g.bulles.map((b) => b.id),
            })),
          }
        : null,
    [groupesLus, groupes]
  );

  const loupe = useMemo<LoupeDeLaCarte>(() => {
    const allumee = enAnalyse ? surlignee : null;
    const duGroupe = enAnalyse && groupes ? groupeVu : null;
    if (!infos && !recherche?.trim() && !contact && !analyse && !allumee && !duGroupe) return SANS_LOUPE;
    const allumees =
      contact || allumee || duGroupe
        ? new Set([...(contact?.bulles ?? []), ...(duGroupe?.bulles ?? []), ...(allumee ? [allumee] : [])])
        : null;
    return {
      infoDe: infos ? (bulle) => infosAuLieuDuTitre(bulle, infos.infos) : null,
      trouvee: recherche?.trim() ? (bulle) => trouveeDans(bulle, recherche) : null,
      allumees,
      analyse,
    };
  }, [infos, recherche, contact, analyse, enAnalyse, surlignee, groupes, groupeVu]);

  return {
    loupe,
    /** A : les liens cachés se montrent sur la carte. */
    liensCachesMontres: surLaCarte && liensCaches,
    recherche,
    setRecherche,
    /** Fermer la recherche (Échap dans son champ) : elle se vide, les bulles reviennent nettes. */
    fermerLaRecherche: () => setRecherche(null),
    champ,
    marges,
    /** Un clic sur un contact du répertoire allume ses bulles ; le même clic les éteint. */
    contactChoisi: contact?.cle ?? null,
    choisirLeContact: (cle: string, bulles: readonly string[]) =>
      setContact((avant) => (avant?.cle === cle ? null : { cle, bulles: new Set(bulles) })),
    /** En Analyse : le panneau d'analyse est-il ouvert ? */
    panneauDAnalyse: enAnalyse && panneau,
    /** En Analyse : la bulle allumée, et ses lignes dans le panneau. */
    surlignee: enAnalyse ? surlignee : null,
    /** Un clic sur une bulle, en Analyse : elle et ses lignes s'allument ; le même clic les éteint. */
    basculerLaBulle,
    /** En Analyse, sur la Carte : le regroupement choisi, ses groupes, et les colonnes de la carte. */
    regroupement: regroupe,
    groupes,
    colonnes,
    /** « Regrouper par » dans le panneau : le même geste que G, à un choix précis ; `null` : rien. */
    regrouper: (par: Regroupement | null) => {
      setRegroupement(par);
      setGroupeVu(null);
    },
    /** Un bloc de groupe survolé : ses bulles s'allument ; une ligne d'une bulle survolée : elle seule. */
    groupeVu: groupes ? (groupeVu?.cle ?? null) : null,
    surlignerLeGroupe: (cle: string, bulles: readonly string[]) => {
      setGroupeVu({ cle, bulles: new Set(bulles) });
      setSurlignee(null);
    },
    /** Une ligne du panneau survolée (une échéance, une étape, une bulle d'un groupe) : sa bulle s'allume. */
    surlignerUneBulle: (id: string) => {
      setGroupeVu(null);
      setSurlignee(id);
    },
  };
}
